import { useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { CheckboxCard } from "@/components/CheckboxCard";
import { FormActions } from "@jbnl/shared-ui";
import { SelectAllCheckbox } from "@/components/SelectAllCheckbox";
import { Button } from "@/components/ui/button";
import { FieldDescription, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { datumKort } from "@/features/wedstrijden/datum";
import {
  useKlassen,
  type Wedstrijddag,
} from "@/features/wedstrijden/useWedstrijden";
import {
  DOELGROEP,
  NO_VOORKEUR_SETTINGS,
  DEFAULT_VOORKEUR_SETTINGS,
  VOORKEUR_LIMIT,
  type Doelgroep,
  type Inschrijfperiode,
  type PeriodeVelden,
} from "./useInschrijfperiodes";
import { naarInvoer, naarIso } from "./venster";

const toggle = (huidig: string[], value: string) =>
  huidig.includes(value)
    ? huidig.filter((id) => id !== value)
    : [...huidig, value];

function WedstrijddagenVeld({
  id,
  dagen,
  wedstrijddagIds,
  setWedstrijddagIds,
  voorKorpsen,
}: {
  id: string;
  dagen: Wedstrijddag[];
  wedstrijddagIds: string[];
  setWedstrijddagIds: Dispatch<SetStateAction<string[]>>;
  voorKorpsen: boolean;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);

  // A dag with no organiserend korps cannot be opened up, and the server says so. Both
  // doelgroepen are being asked to commit to a place - a korps picks a voorkeursdatum by how far
  // it has to drive, an ondersteuner offers a day at a kazerne - and neither question can be put
  // about a dag that has no address yet. Left out of the list rather than drawn and refused on
  // save, with a line below saying how many are waiting on a korps.
  const openstelbaar = dagen.filter((dag) => dag.organiserendKorpsName);
  const zonderKorps = dagen.length - openstelbaar.length;

  // Counted against what is on the screen rather than against the state, so "alles" means the
  // dagen this form is drawing and never a stale id the seizoen no longer offers.
  const gekozenDagen = openstelbaar.filter((dag) =>
    wedstrijddagIds.includes(dag.id),
  );

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("form.wedstrijddagen")}</FieldLegend>
      {openstelbaar.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {dagen.length === 0
            ? t("form.geenDagen")
            : t("form.geenOpenstelbareDagen")}
        </p>
      ) : (
        <>
          <SelectAllCheckbox
            id={`${id}-dagen-alles`}
            total={openstelbaar.length}
            selected={gekozenDagen.length}
            onChange={(alles) =>
              setWedstrijddagIds(alles ? openstelbaar.map((dag) => dag.id) : [])
            }
          />
          <ul className="grid gap-2 sm:grid-cols-2">
            {openstelbaar.map((dag) => (
              <li key={dag.id}>
                <CheckboxCard
                  id={`${id}-dag-${dag.id}`}
                  title={datumKort(dag.date)}
                  description={t("form.dagRegel", {
                    niveau: dag.niveauName,
                    korps: dag.organiserendKorpsName ?? "",
                  })}
                  checked={wedstrijddagIds.includes(dag.id)}
                  onCheckedChange={() =>
                    setWedstrijddagIds((huidig) => toggle(huidig, dag.id))
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {zonderKorps > 0 && (
        <p className="text-sm text-muted-foreground">
          {t("form.zonderKorps", { count: zonderKorps })}
        </p>
      )}

      {/* Unconditional on a korpsperiode. Narrowing it to the periodes it is really about
          would mean deciding that these dagen sit above kwalificatie, which nothing in the
          model can say - see the key's own note and ADR-0058. */}
      {voorKorpsen && (
        <p className="text-sm text-muted-foreground">{t("form.promoties")}</p>
      )}
    </FieldSet>
  );
}

// A checkbox beside the number rather than zero meaning off, so the number survives.
function VoorkeurVeld({
  checkboxId,
  titel,
  inputId,
  labelTekst,
  checked,
  onToggle,
  waarde,
  onWaardeChange,
  teHoog,
  foutmelding,
}: {
  checkboxId: string;
  titel: string;
  inputId: string;
  labelTekst: string;
  checked: boolean;
  onToggle: () => void;
  waarde: number;
  onWaardeChange: (waarde: number) => void;
  teHoog: boolean;
  foutmelding: string;
}) {
  return (
    <li className="grid gap-2">
      <CheckboxCard
        id={checkboxId}
        title={titel}
        checked={checked}
        onCheckedChange={onToggle}
      />
      <Label htmlFor={inputId}>{labelTekst}</Label>
      <Input
        id={inputId}
        type="number"
        min={1}
        max={VOORKEUR_LIMIT}
        value={waarde}
        disabled={!checked}
        aria-invalid={teHoog}
        onChange={(event) => onWaardeChange(Number(event.target.value))}
      />
      {teHoog && <p className="text-sm text-destructive">{foutmelding}</p>}
    </li>
  );
}

/**
 * The klassen half is korps-only: an ondersteuner's functie comes from their bevoegdheden, so a
 * klasse there is configuration nothing reads back. The server refuses it rather than stripping it.
 */
export function InschrijfperiodeForm({
  id,
  periode,
  dagen,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  id: string;
  periode?: Inschrijfperiode;
  dagen: Wedstrijddag[];
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (velden: PeriodeVelden) => void;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);
  const { data: klassen } = useKlassen();

  const [name, setName] = useState(periode?.name ?? "");
  const [targetGroup, setTargetGroup] = useState<Doelgroep>(
    (periode?.targetGroup as Doelgroep) ?? DOELGROEP.korpsen,
  );
  const [opensOn, setOpensOn] = useState(
    periode ? naarInvoer(periode.opensOn) : "",
  );
  const [closesOn, setClosesOn] = useState(
    periode ? naarInvoer(periode.closesOn) : "",
  );
  const [wedstrijddagIds, setWedstrijddagIds] = useState<string[]>(
    periode?.wedstrijddagIds ?? [],
  );
  const [klasseIds, setKlasseIds] = useState<string[]>(
    periode?.klasseIds ?? [],
  );

  const [voorkeuren, setVoorkeuren] = useState(() =>
    periode
      ? {
          voorkeurenEnabled: periode.voorkeurenEnabled,
          geenVoorkeurEnabled: periode.geenVoorkeurEnabled,
          maxVoorkeuren: Number(periode.maxVoorkeuren),
          maxGeenVoorkeuren: Number(periode.maxGeenVoorkeuren),
        }
      : DEFAULT_VOORKEUR_SETTINGS,
  );

  const voorKorpsen = targetGroup === DOELGROEP.korpsen;

  /**
   * Liever niet stops one short, or a ploeg is configured to be unplaceable. Said beside the
   * field rather than enforced: disabling it opened a new periode with two dead inputs.
   */
  const dagenGekozen = wedstrijddagIds.length;
  const maxVoorkeurenPlafond = Math.min(VOORKEUR_LIMIT, dagenGekozen);
  const maxGeenVoorkeurPlafond = Math.min(
    VOORKEUR_LIMIT,
    Math.max(0, dagenGekozen - 1),
  );

  const voorkeurenTeHoog =
    voorkeuren.voorkeurenEnabled &&
    voorkeuren.maxVoorkeuren > maxVoorkeurenPlafond;

  const geenVoorkeurTeHoog =
    voorkeuren.geenVoorkeurEnabled &&
    voorkeuren.maxGeenVoorkeuren > maxGeenVoorkeurPlafond;

  const gekozenKlassen = (klassen ?? []).filter((klasse) =>
    klasseIds.includes(klasse.id),
  );

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          name,
          targetGroup,
          opensOn: naarIso(opensOn),
          closesOn: naarIso(closesOn),
          wedstrijddagIds,
          // Never sent for ondersteuners, whatever is in state: switching the doelgroep back
          // and forth would otherwise post klassen the beheerder cannot see on the screen.
          klasseIds: voorKorpsen ? klasseIds : [],
          // The same rule one field over. An ondersteuner periode may carry nothing but
          // all-off, and the server refuses anything else rather than stripping it.
          //
          // Sent as typed rather than clamped to the ceiling. A clamp would change a beheerder's
          // answer without saying so; the submit is blocked while either number is too high and
          // the field says by how much.
          voorkeuren: voorKorpsen ? voorkeuren : NO_VOORKEUR_SETTINGS,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor={`${id}-naam`}>{t("form.naam")}</Label>
          <Input
            id={`${id}-naam`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("form.naamPlaceholder")}
            required
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-doelgroep`}>{t("form.doelgroep")}</Label>
          {/* A select, and the shortest fixed vocabulary there is: two values, and never a
              list of records anyone browses. */}
          <Select
            id={`${id}-doelgroep`}
            value={String(targetGroup)}
            onChange={(event) =>
              setTargetGroup(Number(event.target.value) as Doelgroep)
            }
          >
            <option value={DOELGROEP.korpsen}>{t("doelgroep.korpsen")}</option>
            <option value={DOELGROEP.ondersteuners}>
              {t("doelgroep.ondersteuners")}
            </option>
          </Select>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor={`${id}-opent`}>{t("form.opent")}</Label>
          {/* Date and time: "31 december" is ambiguous by a day, and the server compares to the tick. */}
          <Input
            id={`${id}-opent`}
            type="datetime-local"
            value={opensOn}
            onChange={(event) => setOpensOn(event.target.value)}
            required
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-sluit`}>{t("form.sluit")}</Label>
          <Input
            id={`${id}-sluit`}
            type="datetime-local"
            value={closesOn}
            onChange={(event) => setClosesOn(event.target.value)}
            required
          />
        </div>
      </div>

      <WedstrijddagenVeld
        id={id}
        dagen={dagen}
        wedstrijddagIds={wedstrijddagIds}
        setWedstrijddagIds={setWedstrijddagIds}
        voorKorpsen={voorKorpsen}
      />

      {voorKorpsen && (
        <FieldSet>
          <FieldLegend variant="label">{t("form.klassen")}</FieldLegend>
          <SelectAllCheckbox
            id={`${id}-klassen-alles`}
            total={(klassen ?? []).length}
            selected={gekozenKlassen.length}
            onChange={(alles) =>
              setKlasseIds(
                alles ? (klassen ?? []).map((klasse) => klasse.id) : [],
              )
            }
          />
          {/* Cards, as the wedstrijddagen above are: one form, one way of ticking a box. */}
          <ul className="grid gap-2 sm:grid-cols-2">
            {(klassen ?? []).map((klasse) => (
              <li key={klasse.id}>
                <CheckboxCard
                  id={`${id}-klasse-${klasse.id}`}
                  title={klasse.name}
                  checked={klasseIds.includes(klasse.id)}
                  onCheckedChange={() =>
                    setKlasseIds((huidig) => toggle(huidig, klasse.id))
                  }
                />
              </li>
            ))}
          </ul>
          <FieldDescription>{t("form.klassenUitleg")}</FieldDescription>
        </FieldSet>
      )}

      {/* Korps-only, like the klassen: an ondersteuner is never asked what it thinks of a dag. */}
      {voorKorpsen && (
        <FieldSet>
          <FieldLegend variant="label">{t("form.voorkeuren")}</FieldLegend>
          <ul className="grid gap-2 sm:grid-cols-2">
            <VoorkeurVeld
              checkboxId={`${id}-voorkeuren-aan`}
              titel={t("form.voorkeurenAan")}
              inputId={`${id}-max-voorkeuren`}
              labelTekst={t("form.maxVoorkeuren")}
              checked={voorkeuren.voorkeurenEnabled}
              onToggle={() =>
                setVoorkeuren((huidig) => ({
                  ...huidig,
                  voorkeurenEnabled: !huidig.voorkeurenEnabled,
                }))
              }
              waarde={voorkeuren.maxVoorkeuren}
              onWaardeChange={(waarde) =>
                setVoorkeuren((huidig) => ({
                  ...huidig,
                  maxVoorkeuren: waarde,
                }))
              }
              teHoog={voorkeurenTeHoog}
              foutmelding={
                maxVoorkeurenPlafond === 0
                  ? t("form.voorkeurenGeenDagen")
                  : t("form.voorkeurenTeHoog", { count: maxVoorkeurenPlafond })
              }
            />

            <VoorkeurVeld
              checkboxId={`${id}-geen-voorkeur-aan`}
              titel={t("form.geenVoorkeurAan")}
              inputId={`${id}-max-geen-voorkeur`}
              labelTekst={t("form.maxGeenVoorkeuren")}
              checked={voorkeuren.geenVoorkeurEnabled}
              onToggle={() =>
                setVoorkeuren((huidig) => ({
                  ...huidig,
                  geenVoorkeurEnabled: !huidig.geenVoorkeurEnabled,
                }))
              }
              waarde={voorkeuren.maxGeenVoorkeuren}
              onWaardeChange={(waarde) =>
                setVoorkeuren((huidig) => ({
                  ...huidig,
                  maxGeenVoorkeuren: waarde,
                }))
              }
              teHoog={geenVoorkeurTeHoog}
              foutmelding={
                maxGeenVoorkeurPlafond === 0
                  ? t("form.geenVoorkeurGeenDagen")
                  : t("form.geenVoorkeurTeHoog", {
                      count: maxGeenVoorkeurPlafond,
                    })
              }
            />
          </ul>
          <FieldDescription>{t("form.voorkeurenUitleg")}</FieldDescription>
        </FieldSet>
      )}

      <FormActions>
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Button
          type="submit"
          disabled={pending || voorkeurenTeHoog || geenVoorkeurTeHoog}
        >
          {pending ? t("common:actions.busy") : submitLabel}
        </Button>
      </FormActions>
    </form>
  );
}
